import { AlertTriangle, Loader2, Trash2, ShieldAlert } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

export interface DeleteConfirmationDialogProps {
  open: boolean;
  title: string;
  description: string;
  itemName?: string;
  warningText?: string;
  isBlocked?: boolean;
  blockMessage?: string;
  isDeleting?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

const DeleteConfirmationDialog = ({
  open,
  title,
  description,
  itemName,
  warningText,
  isBlocked = false,
  blockMessage,
  isDeleting = false,
  onConfirm,
  onCancel,
}: DeleteConfirmationDialogProps) => (
  <Dialog open={open} onOpenChange={(o) => { if (!o) onCancel(); }}>
    <DialogContent className="max-w-md">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2 text-destructive">
          <AlertTriangle className="w-5 h-5" /> {title}
        </DialogTitle>
        <DialogDescription>{description}</DialogDescription>
      </DialogHeader>

      {itemName && (
        <p className="text-sm font-medium text-foreground break-words">{itemName}</p>
      )}

      {warningText && (
        <p className="text-sm text-amber-600 dark:text-amber-400 flex items-center gap-1.5">
          <AlertTriangle className="w-4 h-4 shrink-0" />
          {warningText}
        </p>
      )}

      {isBlocked && blockMessage && (
        <div className="rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive flex items-start gap-2">
          <ShieldAlert className="w-4 h-4 shrink-0 mt-0.5" />
          <span>{blockMessage}</span>
        </div>
      )}

      <div className="flex justify-end gap-3 mt-2">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          disabled={isDeleting}
        >
          Cancel
        </Button>
        <Button
          type="button"
          variant="destructive"
          onClick={onConfirm}
          disabled={isDeleting || isBlocked}
          className="gap-1.5"
        >
          {isDeleting ? (
            <><Loader2 className="w-4 h-4 animate-spin" /> Deleting...</>
          ) : (
            <><Trash2 className="w-4 h-4" /> Delete</>
          )}
        </Button>
      </div>
    </DialogContent>
  </Dialog>
);

export default DeleteConfirmationDialog;
